import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  type ErrorComponentProps,
  Outlet,
  useRouter,
} from "@tanstack/react-router";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import { ErrorState, NotFoundState } from "@/components/common/states";

export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * The root. It owns the two states the app must never render as a blank page:
 * a route that does not exist, and a loader that threw.
 *
 * The screenshot suite asserts a 200 and the screen's own `h1` for every
 * screen, so a route whose loader throws fails the capture rather than
 * shipping a picture of an error page — but only if the error is visible. A
 * swallowed error renders an empty panel, which looks like a design.
 */
function Root() {
  // nuqs keeps filters, tabs and open panels in the URL, so every state is a
  // link — which is what makes the screenshot suite possible. Its TanStack
  // adapter reads the router from context, so it mounts inside the route tree
  // rather than around `RouterProvider`; outside it, `useRouter()` returns
  // null and the first `useQueryState` throws on `null.history`.
  //
  // It also means no Suspense boundary per component. In the Next app every
  // component that read the query string needed its own, or the static
  // prerender bailed out of the whole page.
  return (
    <NuqsAdapter>
      <Outlet />
    </NuqsAdapter>
  );
}

function RootError({ error }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <main className="flex min-h-svh items-center justify-center px-6">
      <ErrorState
        title="The dashboard could not load"
        error={error}
        onRetry={() => {
          void router.invalidate();
        }}
      />
    </main>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: () => (
    <NotFoundState description="The link may be stale, or the id may belong to another project." />
  ),
  errorComponent: RootError,
});
